import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  templateThumbnailUrl,
  useDeleteTemplateMutation,
  useUpdateTemplateMutation,
} from "@/api/templates";
import { useAuthStore } from "@/stores/auth";
import { applySvgFields } from "@/lib/svgFields";
import type { FieldDefinition, Template } from "@/types/template";

interface TemplateListRowProps {
  template: Template;
}

/** Data-URL of an SVG template at its default field values (schema defaults
 *  overlaid with default_props). Null for non-SVG templates. */
function useSvgCardUrl(template: Template): string | null {
  return useMemo(() => {
    if (template.project_kind !== "svg") return null;
    const defaults: Record<string, unknown> = {};
    const schema: FieldDefinition[] = Array.isArray(template.field_schema)
      ? template.field_schema
      : [];
    for (const f of schema) defaults[f.key] = f.default;
    if (template.default_props && typeof template.default_props === "object") {
      Object.assign(defaults, template.default_props);
    }
    const svg = applySvgFields(template.tsx_code, defaults);
    // Standard percent-encoded form (NOT `;utf8,`): the browser decodes it
    // before parsing, so `#` in url(#id)/hex colors survives as %23 → #.
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }, [template]);
}

/**
 * Single row in the list view. Uses the same data model as `TemplateCard`
 * (thumbnail URL, kebab actions) but lays out horizontally — small
 * thumbnail on the left, name + meta in the middle, kebab on the right.
 *
 * The whole row is clickable (Link wrapper) like Google Drive's list
 * view. Kebab clicks call e.stopPropagation so they don't trigger nav.
 */
export function TemplateListRow({ template }: TemplateListRowProps) {
  const currentUser = useAuthStore((s) => s.user);
  const isOwner =
    currentUser != null && template.user_id === currentUser.id;

  const [thumbOk, setThumbOk] = useState(true);
  const thumbUrl = templateThumbnailUrl(template);
  const svgUrl = useSvgCardUrl(template);

  // Format updated_at as a short human date (Oct 12 vs full ISO).
  const updated = (() => {
    try {
      const d = new Date(template.updated_at);
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
      });
    } catch {
      return "";
    }
  })();

  return (
    <div className="group relative flex items-center gap-3 rounded-lg border border-line bg-surface-2 p-2 transition-colors hover:border-line-strong hover:bg-surface-3">
      <Link
        to="/templates/$id"
        params={{ id: template.id }}
        className="flex flex-1 items-center gap-3 min-w-0"
      >
        {/* Thumbnail — small (16:9 at h-12) */}
        <div className="relative aspect-video h-12 shrink-0 overflow-hidden rounded bg-surface-3">
          {svgUrl ? (
            // SVG: substituted-defaults graphic directly (no PNG thumbnail).
            <img
              src={svgUrl}
              alt=""
              loading="lazy"
              className="absolute inset-0 h-full w-full object-contain p-1"
            />
          ) : thumbOk ? (
            <img
              src={thumbUrl}
              alt=""
              loading="lazy"
              onError={() => setThumbOk(false)}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-ink-3">
              MF
            </div>
          )}
          {svgUrl && (
            <div className="absolute left-0.5 top-0.5 z-10 rounded bg-surface/80 px-1 text-[9px] font-medium text-ink-2 backdrop-blur">
              SVG
            </div>
          )}
        </div>

        {/* Name + description */}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-ink group-hover:text-accent">
            {template.name}
          </div>
          {template.description && (
            <div className="truncate text-xs text-ink-3">
              {template.description}
            </div>
          )}
        </div>

        {/* Meta — category + dimensions, hidden on narrow viewports */}
        <div className="hidden shrink-0 items-center gap-3 text-xs text-ink-2 sm:flex">
          <span className="rounded bg-surface-3 px-2 py-0.5">
            {template.category}
          </span>
          <span>v{template.version}</span>
          <span className="hidden md:inline">
            {template.width}×{template.height}
          </span>
          <span className="hidden lg:inline w-20 text-right">{updated}</span>
        </div>
      </Link>

      {/* Kebab — sibling of the link so its click doesn't navigate.
          Owner-only (matches grid card behavior). */}
      {isOwner && <RowMenu template={template} />}
    </div>
  );
}

/**
 * Compact row-level kebab. Mirrors TemplateCard's menu wholesale —
 * Rename + Delete. Kept inline (not extracted) because the header
 * spacing differs from the card variant.
 */
function RowMenu({ template }: { template: Template }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const updateMutation = useUpdateTemplateMutation(template.id);
  const deleteMutation = useDeleteTemplateMutation();

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const stop = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-label="Template actions"
        onClick={(e) => {
          stop(e);
          setOpen((v) => !v);
        }}
        className="flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-sm text-ink-2 transition-colors hover:border-line hover:bg-surface-3 hover:text-ink"
      >
        ⋯
      </button>
      {open && (
        <div
          onClick={stop}
          className="absolute right-0 top-full z-20 mt-1 min-w-[140px] overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg"
        >
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              setOpen(false);
              const next = window.prompt("Rename template", template.name);
              if (next == null) return;
              const trimmed = next.trim();
              if (!trimmed || trimmed === template.name) return;
              updateMutation.mutate({ name: trimmed });
            }}
            className="block w-full px-3 py-1.5 text-left text-sm text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
          >
            Rename
          </button>
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              setOpen(false);
              const ok = window.confirm(
                `Delete "${template.name}"? This removes it from your dashboard. Existing renders are preserved.`,
              );
              if (!ok) return;
              deleteMutation.mutate(template.id);
            }}
            className="block w-full px-3 py-1.5 text-left text-sm text-danger transition-colors hover:bg-danger-soft"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
